import { useState, useEffect, useCallback } from 'react';
import { apiFetch, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Package, Plus, Pencil, Trash2, ShoppingBag, Truck,
  Search, Download, Eye, EyeOff, X, ChevronDown, ChevronUp, Settings2, Tag
} from 'lucide-react';

const TABS = ['products', 'orders', 'discounts', 'settings'];

const StoreTab = () => {
  const [subTab, setSubTab] = useState('products');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    apiFetch(`${API_BASE}/store/admin/analytics`).then(r => r.ok ? r.json() : null).then(setAnalytics).catch(() => {});
  }, []);

  return (
    <div data-testid="store-tab">
      {/* Quick Stats */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Revenue', value: `$${analytics.total_revenue.toFixed(2)}`, color: 'text-green-600' },
            { label: 'Orders', value: analytics.total_orders, color: 'text-blue-600' },
            { label: 'Unfulfilled', value: analytics.unfulfilled_orders, color: analytics.unfulfilled_orders > 0 ? 'text-red-600' : 'text-gray-600' },
            { label: 'Products', value: `${analytics.active_products} active`, color: 'text-gray-600' },
          ].map(s => (
            <div key={s.label} className="bg-white p-4 border">
              <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Sub Tabs */}
      <div className="flex gap-2 mb-6" data-testid="store-sub-tabs">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setSubTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${subTab === t ? 'bg-brand-dark text-white' : 'bg-white text-brand-dark hover:bg-gray-50'}`}
            data-testid={`store-sub-${t}`}
          >
            {t === 'products' && <Package className="w-4 h-4 inline mr-1.5" />}
            {t === 'orders' && <ShoppingBag className="w-4 h-4 inline mr-1.5" />}
            {t === 'discounts' && <Tag className="w-4 h-4 inline mr-1.5" />}
            {t === 'settings' && <Settings2 className="w-4 h-4 inline mr-1.5" />}
            {t}
          </button>
        ))}
      </div>

      {subTab === 'products' && <ProductsPanel />}
      {subTab === 'orders' && <OrdersPanel />}
      {subTab === 'discounts' && <DiscountCodesPanel />}
      {subTab === 'settings' && <StoreSettingsPanel />}
    </div>
  );
};


// ===== PRODUCTS PANEL =====
const ProductsPanel = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/store/products?active_only=false`);
      if (res.ok) setProducts(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await apiFetch(`${API_BASE}/store/admin/products/${id}`, { method: 'DELETE' });
    fetchProducts();
  };

  const handleToggle = async (product) => {
    await apiFetch(`${API_BASE}/store/admin/products/${product.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !product.is_active }),
    });
    fetchProducts();
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="product-search" />
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Product
        </Button>
      </div>

      {showForm && (
        <ProductForm
          product={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchProducts(); }}
        />
      )}

      {loading ? (
        <p className="text-gray-500 py-8 text-center">Loading products...</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500 py-8 text-center">No products found. Add your first product!</p>
      ) : (
        <div className="grid gap-4">
          {filtered.map(p => (
            <div key={p.id} className={`bg-white border p-4 flex gap-4 items-start ${!p.is_active ? 'opacity-60' : ''}`} data-testid={`product-row-${p.id}`}>
              <div className="w-16 h-16 bg-gray-100 flex-shrink-0 overflow-hidden">
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-gray-300" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-brand-dark truncate">{p.name}</h4>
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 capitalize">{p.category}</span>
                  {!p.is_active && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600">Hidden</span>}
                </div>
                <p className="text-lg font-bold text-green-700">${p.price.toFixed(2)}</p>
                <p className="text-xs text-gray-500">
                  {p.has_variants
                    ? `${p.variants?.length || 0} variants | Total stock: ${(p.variants || []).reduce((s, v) => s + (v.stock || 0), 0)}`
                    : `Stock: ${p.stock}`}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => handleToggle(p)} className="p-2 hover:bg-gray-100 rounded" title={p.is_active ? 'Hide' : 'Show'}>
                  {p.is_active ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                </button>
                <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-2 hover:bg-gray-100 rounded" data-testid={`edit-product-${p.id}`}>
                  <Pencil className="w-4 h-4 text-blue-600" />
                </button>
                <button onClick={() => handleDelete(p.id)} className="p-2 hover:bg-gray-100 rounded" data-testid={`delete-product-${p.id}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ===== PRODUCT FORM =====
const ProductForm = ({ product, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    category: product?.category || 'other',
    has_variants: product?.has_variants || false,
    variants: product?.variants || [],
    stock: product?.stock || 0,
    images: product?.images || [],
  });
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiFetch(`${API_BASE}/upload`, { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm(f => ({ ...f, images: [...f.images, data.url] }));
      }
    } catch {}
    setUploading(false);
  };

  const addImageUrl = () => {
    if (!imageUrl.trim()) return;
    setForm(f => ({ ...f, images: [...f.images, imageUrl.trim()] }));
    setImageUrl('');
  };

  const removeImage = (idx) => {
    setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const addVariant = () => {
    setForm(f => ({ ...f, variants: [...f.variants, { label: '', stock: 0 }] }));
  };

  const updateVariant = (idx, field, value) => {
    setForm(f => {
      const v = [...f.variants];
      v[idx] = { ...v[idx], [field]: field === 'stock' ? parseInt(value) || 0 : value };
      return { ...f, variants: v };
    });
  };

  const removeVariant = (idx) => {
    setForm(f => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 0 };

    try {
      const url = product ? `${API_BASE}/store/admin/products/${product.id}` : `${API_BASE}/store/admin/products`;
      const method = product ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) onSaved();
    } catch {}
    setSaving(false);
  };

  return (
    <div className="bg-white border p-6 mb-6" data-testid="product-form">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-brand-dark">{product ? 'Edit Product' : 'New Product'}</h3>
        <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Name *</Label>
            <Input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="product-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Price ($) *</Label>
              <Input type="number" step="0.01" min="0" required value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} data-testid="product-price-input" />
            </div>
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="product-category-select">
                <option value="apparel">Apparel</option>
                <option value="accessories">Accessories</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" data-testid="product-desc-input" />
        </div>

        {/* Images */}
        <div>
          <Label>Images</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {form.images.map((img, i) => (
              <div key={i} className="relative w-20 h-20 border overflow-hidden group">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-red-500 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input type="file" accept="image/*" onChange={handleUpload} className="flex-1" disabled={uploading} />
            <div className="flex gap-1">
              <Input placeholder="Or paste URL" value={imageUrl} onChange={e => setImageUrl(e.target.value)} className="w-48" />
              <Button type="button" variant="outline" size="sm" onClick={addImageUrl}>Add</Button>
            </div>
          </div>
          {uploading && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
        </div>

        {/* Variants toggle */}
        <div className="flex items-center gap-3">
          <Switch checked={form.has_variants} onCheckedChange={v => setForm(f => ({ ...f, has_variants: v }))} data-testid="has-variants-switch" />
          <Label>This product has variants (e.g., sizes)</Label>
        </div>

        {form.has_variants ? (
          <div>
            <Label>Variants</Label>
            <div className="space-y-2 mt-1">
              {form.variants.map((v, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input placeholder="Label (e.g. S, M, L)" value={v.label} onChange={e => updateVariant(i, 'label', e.target.value)} className="flex-1" data-testid={`variant-label-${i}`} />
                  <Input type="number" min="0" placeholder="Stock" value={v.stock} onChange={e => updateVariant(i, 'stock', e.target.value)} className="w-24" data-testid={`variant-stock-${i}`} />
                  <button type="button" onClick={() => removeVariant(i)}><X className="w-4 h-4 text-red-400" /></button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addVariant} data-testid="add-variant-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Variant
            </Button>
          </div>
        ) : (
          <div>
            <Label>Stock</Label>
            <Input type="number" min="0" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: parseInt(e.target.value) || 0 }))} className="w-32" data-testid="product-stock-input" />
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={saving} data-testid="product-save-btn">{saving ? 'Saving...' : product ? 'Update Product' : 'Create Product'}</Button>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </div>
  );
};


// ===== ORDERS PANEL =====
const OrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/store/admin/orders`);
      if (res.ok) setOrders(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateOrder = async (orderId, data) => {
    const res = await apiFetch(`${API_BASE}/store/admin/orders/${orderId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (res.ok) fetchOrders();
  };

  const exportCSV = async () => {
    const res = await apiFetch(`${API_BASE}/export/store-orders`);
    if (!res.ok) return;
    const { csv, filename } = await res.json();
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  const filtered = orders.filter(o =>
    o.payment_status === 'paid' && (
      (o.order_number || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.customer_email || '').toLowerCase().includes(search.toLowerCase())
    )
  );

  const statusColors = {
    unfulfilled: 'bg-yellow-100 text-yellow-700',
    shipped: 'bg-blue-100 text-blue-700',
    delivered: 'bg-green-100 text-green-700',
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search orders..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="order-search" />
        </div>
        <Button variant="outline" onClick={exportCSV} data-testid="export-orders-btn">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-500 py-8 text-center">Loading orders...</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500 py-8 text-center">No paid orders yet.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(o => (
            <div key={o.id} className="bg-white border" data-testid={`order-row-${o.id}`}>
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}>
                <div className="flex items-center gap-4">
                  <span className="font-mono font-bold text-brand-dark">{o.order_number}</span>
                  <span className="text-sm text-gray-600">{o.customer_name}</span>
                  <span className={`text-xs px-2 py-0.5 capitalize ${statusColors[o.fulfillment_status] || 'bg-gray-100 text-gray-600'}`}>
                    {o.fulfillment_status}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-green-700">${o.total?.toFixed(2)}</span>
                  {expandedId === o.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>

              {expandedId === o.id && (
                <div className="border-t p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Customer</p>
                      <p className="text-sm font-medium">{o.customer_name}</p>
                      <p className="text-sm text-gray-600">{o.customer_email}</p>
                      {o.customer_phone && <p className="text-sm text-gray-600">{o.customer_phone}</p>}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Shipping Address</p>
                      <p className="text-sm">{o.shipping_address?.line1}</p>
                      {o.shipping_address?.line2 && <p className="text-sm">{o.shipping_address.line2}</p>}
                      <p className="text-sm">{o.shipping_address?.city}, {o.shipping_address?.state} {o.shipping_address?.zip}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Items</p>
                    <div className="space-y-1">
                      {(o.items || []).map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span>{item.product_name}{item.variant_label ? ` (${item.variant_label})` : ''} x{item.quantity}</span>
                          <span className="font-medium">${(item.unit_price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="border-t pt-1 mt-1 flex justify-between text-sm text-gray-500">
                        <span>Shipping</span><span>${o.shipping_fee?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold">
                        <span>Total</span><span>${o.total?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                    <div>
                      <Label className="text-xs">Fulfillment Status</Label>
                      <select
                        value={o.fulfillment_status || 'unfulfilled'}
                        onChange={e => updateOrder(o.id, { fulfillment_status: e.target.value })}
                        className="w-full h-9 border rounded-md px-2 text-sm mt-1"
                        data-testid={`order-status-${o.id}`}
                      >
                        <option value="unfulfilled">Unfulfilled</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Tracking Number</Label>
                      <Input
                        value={o.tracking_number || ''}
                        onChange={e => updateOrder(o.id, { tracking_number: e.target.value })}
                        placeholder="Enter tracking #"
                        className="mt-1"
                        data-testid={`order-tracking-${o.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Notes</Label>
                      <Input
                        value={o.notes || ''}
                        onChange={e => updateOrder(o.id, { notes: e.target.value })}
                        placeholder="Internal notes"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Ordered: {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ===== DISCOUNT CODES PANEL =====
const DiscountCodesPanel = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/store/admin/discount-codes`);
      if (res.ok) setCodes(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this discount code?')) return;
    await apiFetch(`${API_BASE}/store/admin/discount-codes/${id}`, { method: 'DELETE' });
    fetchCodes();
  };

  const handleToggle = async (code) => {
    await apiFetch(`${API_BASE}/store/admin/discount-codes/${code.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !code.is_active }),
    });
    fetchCodes();
  };

  return (
    <div data-testid="discount-codes-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-brand-dark">Store Discount Codes</h3>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-discount-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Code
        </Button>
      </div>

      {showForm && (
        <DiscountCodeForm
          code={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchCodes(); }}
        />
      )}

      {loading ? (
        <p className="text-gray-500 py-8 text-center">Loading...</p>
      ) : codes.length === 0 ? (
        <p className="text-gray-500 py-8 text-center">No discount codes yet.</p>
      ) : (
        <div className="space-y-2">
          {codes.map(c => (
            <div key={c.id} className={`bg-white border p-4 flex items-center justify-between ${!c.is_active ? 'opacity-50' : ''}`} data-testid={`discount-row-${c.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-brand-dark">{c.code}</span>
                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700">
                    {c.discount_type === 'percent' ? `${c.discount_value}% off` : `$${c.discount_value} off`}
                  </span>
                  {!c.is_active && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600">Inactive</span>}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Used: {c.uses}{c.max_uses > 0 ? `/${c.max_uses}` : ' (unlimited)'}
                  {c.min_order > 0 && ` | Min order: $${c.min_order}`}
                  {c.expires_at && ` | Expires: ${new Date(c.expires_at).toLocaleDateString()}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleToggle(c)} className="p-2 hover:bg-gray-100 rounded" title={c.is_active ? 'Deactivate' : 'Activate'}>
                  {c.is_active ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                </button>
                <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-2 hover:bg-gray-100 rounded">
                  <Pencil className="w-4 h-4 text-blue-600" />
                </button>
                <button onClick={() => handleDelete(c.id)} className="p-2 hover:bg-gray-100 rounded">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DiscountCodeForm = ({ code, onClose, onSaved }) => {
  const [form, setForm] = useState({
    code: code?.code || '',
    discount_type: code?.discount_type || 'percent',
    discount_value: code?.discount_value ?? '',
    max_uses: code?.max_uses ?? 0,
    min_order: code?.min_order ?? 0,
    is_active: code?.is_active ?? true,
    expires_at: code?.expires_at ? code.expires_at.slice(0, 10) : '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      discount_value: parseFloat(form.discount_value) || 0,
      max_uses: parseInt(form.max_uses) || 0,
      min_order: parseFloat(form.min_order) || 0,
      expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59Z').toISOString() : '',
    };
    try {
      const url = code ? `${API_BASE}/store/admin/discount-codes/${code.id}` : `${API_BASE}/store/admin/discount-codes`;
      const method = code ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) onSaved();
    } catch {}
    setSaving(false);
  };

  return (
    <div className="bg-white border p-6 mb-4" data-testid="discount-code-form">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-semibold text-brand-dark">{code ? 'Edit Discount Code' : 'New Discount Code'}</h4>
        <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>Code *</Label>
            <Input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="SAVE20" className="font-mono" data-testid="store-discount-code-input" />
          </div>
          <div>
            <Label>Type</Label>
            <select value={form.discount_type} onChange={e => setForm(f => ({ ...f, discount_type: e.target.value }))} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="store-discount-type">
              <option value="percent">Percent (%)</option>
              <option value="fixed">Fixed ($)</option>
            </select>
          </div>
          <div>
            <Label>Value *</Label>
            <Input type="number" step="0.01" min="0" required value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))} placeholder={form.discount_type === 'percent' ? '20' : '5.00'} data-testid="store-discount-value" />
          </div>
          <div>
            <Label>Max Uses</Label>
            <Input type="number" min="0" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} placeholder="0 = unlimited" data-testid="store-discount-max-uses" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label>Min Order ($)</Label>
            <Input type="number" step="0.01" min="0" value={form.min_order} onChange={e => setForm(f => ({ ...f, min_order: e.target.value }))} placeholder="0" />
          </div>
          <div>
            <Label>Expires</Label>
            <Input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
            <Label>Active</Label>
          </div>
        </div>
        <div className="flex gap-3">
          <Button type="submit" disabled={saving} data-testid="save-discount-btn">{saving ? 'Saving...' : code ? 'Update' : 'Create'}</Button>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </div>
  );
};


// ===== STORE SETTINGS PANEL =====
const StoreSettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch(`${API_BASE}/store/settings`).then(r => r.ok ? r.json() : null).then(setSettings).catch(() => {});
  }, []);

  const save = async (updates) => {
    setSaving(true);
    const res = await apiFetch(`${API_BASE}/store/admin/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (res.ok) setSettings(await res.json());
    setSaving(false);
  };

  if (!settings) return <p className="text-gray-500 py-8 text-center">Loading settings...</p>;

  return (
    <div className="bg-white border p-6 max-w-lg" data-testid="store-settings-panel">
      <h3 className="text-lg font-semibold text-brand-dark mb-4">Store Settings</h3>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="font-medium">Store Enabled</Label>
            <p className="text-xs text-gray-500">When off, the shop page shows a closed message</p>
          </div>
          <Switch checked={settings.store_enabled} onCheckedChange={v => save({ store_enabled: v })} data-testid="store-enabled-switch" />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <Label className="font-medium">Show "Shop" Link in Navigation</Label>
            <p className="text-xs text-gray-500">Toggle the Shop link on/off in the website header</p>
          </div>
          <Switch checked={settings.show_shop_link !== false} onCheckedChange={v => save({ show_shop_link: v })} data-testid="show-shop-link-switch" />
        </div>

        <div>
          <Label>Closed Message</Label>
          <Input value={settings.store_closed_message || ''} onChange={e => setSettings(s => ({ ...s, store_closed_message: e.target.value }))} onBlur={() => save({ store_closed_message: settings.store_closed_message })} className="mt-1" data-testid="store-closed-msg" />
        </div>

        <div>
          <Label>Flat Rate Shipping ($)</Label>
          <Input type="number" step="0.01" min="0" value={settings.shipping_fee ?? 5} onChange={e => setSettings(s => ({ ...s, shipping_fee: parseFloat(e.target.value) || 0 }))} onBlur={() => save({ shipping_fee: settings.shipping_fee })} className="mt-1 w-32" data-testid="shipping-fee-input" />
        </div>

        <div>
          <Label>Free Shipping Threshold ($)</Label>
          <p className="text-xs text-gray-500 mb-1">Orders above this amount get free shipping. Set 0 to disable.</p>
          <Input type="number" step="0.01" min="0" value={settings.free_shipping_threshold ?? 0} onChange={e => setSettings(s => ({ ...s, free_shipping_threshold: parseFloat(e.target.value) || 0 }))} onBlur={() => save({ free_shipping_threshold: settings.free_shipping_threshold })} className="mt-1 w-32" data-testid="free-shipping-input" />
        </div>
      </div>
      {saving && <p className="text-xs text-gray-500 mt-3">Saving...</p>}
    </div>
  );
};

export default StoreTab;
